import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useTheme } from "../lib/theme";
import { useAuth, formatApiError } from "../lib/auth";
import { Key, Sparkle, CheckCircle, Warning, ArrowSquareOut, Trash, Sun, Moon, ImageSquare, Users, ShareNetwork, Plus, Copy, MicrosoftExcelLogo, DownloadSimple, UploadSimple, ShieldCheck, Clock, TelegramLogo, ArrowsClockwise, X, MapPin as MapPinIcon } from "@phosphor-icons/react";

// Quote giornaliere indicative del piano FREE di Google AI Studio
// (fonte: ai.google.dev/pricing — possono cambiare nel tempo).
// I model ID qui sotto sono quelli ufficiali Google attualmente attivi
// nell'API gratuita. NON usare nomi "preview" non documentati: vengono
// rifiutati dall'API e l'identificazione fallisce sempre.
//
// I modelli con `ownerOnly: true` sono riservati all'utente amministratore
// (owner) — sono modelli più pesanti/nuovi o con quota strette che non
// vogliamo esporre agli editor. Il backend applica un doppio controllo:
// (a) PUT /settings rifiuta 403 se un non-owner tenta di salvarli,
// (b) get_active_gemini_key fa silent-downgrade a Flash in fase di identify.
const MODELS = [
  {
    id: "gemini-2.5-flash",
    label: "Gemini 2.5 Flash — bilanciato (consigliato)",
    short: "Bilanciato",
    quota: "~1.500 identificazioni/giorno",
    description:
      "Il cavallo di battaglia per il 90% delle scansioni quotidiane. Buon compromesso tra accuratezza e velocità (3-8s per identificazione), quota giornaliera molto ampia. Gestisce ottimamente monete, scritte latine e reperti standard.",
  },
  {
    id: "gemini-3.1-flash-lite",
    label: "Gemini 3.1 Flash-Lite — quota massima (fallback automatico)",
    short: "Quota massima",
    quota: "~3.000 identificazioni/giorno",
    description:
      "Modello ad altissima quota e bassa latenza. Usato automaticamente dall'app come fallback di sicurezza quando la quota del 2.5 Flash si esaurisce, così l'identificazione non si blocca mai. Selezionalo manualmente per catalogare grandi quantità di reperti.",
  },
  {
    id: "gemini-3.5-flash",
    label: "Gemini 3.5 Flash — alta qualità (admin)",
    short: "Frontier · admin only",
    quota: "~500-1.000 identificazioni/giorno",
    ownerOnly: true,
    description:
      "Modello di ultima generazione, precisione visiva e logica superiore ai vecchi Pro. Ideale per reperti difficili, usurati o iscrizioni quasi illeggibili. Quota inferiore ma qualità massima.",
  },
];

const PHOTO_PRESETS = [
  { label: "Massima qualità (1600px, q88)", side: 1600, q: 88 },
  { label: "Bilanciato (1200px, q82)", side: 1200, q: 82 },
  { label: "Risparmio spazio (1000px, q75)", side: 1000, q: 75 },
];

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [settings, setSettings] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [model, setModel] = useState("gemini-2.5-flash");
  const [keyPreference, setKeyPreference] = useState("shared");
  const [photoSide, setPhotoSide] = useState(1600);
  const [photoQuality, setPhotoQuality] = useState(88);
  const [photoInputMode, setPhotoInputMode] = useState("file");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Users
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "" });
  const [userMsg, setUserMsg] = useState(null);

  // Guest links
  const [guestLinks, setGuestLinks] = useState([]);
  const [newGuestLabel, setNewGuestLabel] = useState("");
  const [copiedToken, setCopiedToken] = useState("");

  // Owner: temp password reveal modal + Telegram contact field
  const [resetResult, setResetResult] = useState(null); // { email, password }
  const [resetCopied, setResetCopied] = useState(false);
  const [ownerTelegram, setOwnerTelegram] = useState("");

  // Excel export / import
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importDryRun, setImportDryRun] = useState(true);

  // Bulk reclassification (fixes historical devotional/anello misclassifications
  // after prompt/normalize_category updates).
  const [reclassifyLoading, setReclassifyLoading] = useState(false);
  const [reclassifyPreview, setReclassifyPreview] = useState(null); // {changes:[], would_change, applied}

  // Active settings section — drives the left sidebar navigation.
  // Stored as a simple string; defaults to "appearance".
  // Available sections: appearance, ai, photos (owner), maps, users (owner),
  // sharing (owner), backup (owner).
  const [activeSection, setActiveSection] = useState("appearance");

  const exportCatalog = async () => {
    setExporting(true);
    try {
      const res = await api.get("/catalog/export", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `reperti_catalogo_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Errore esportazione: " + formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const importCatalog = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", importDryRun ? "true" : "false");
      const { data } = await api.post("/catalog/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setImportResult(data);
    } catch (err) {
      console.error(err);
      setImportResult({ error: formatApiError(err) });
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    if (data.gemini_model) setModel(data.gemini_model);
    if (data.gemini_key_preference) setKeyPreference(data.gemini_key_preference);
    if (data.photo_max_side) setPhotoSide(data.photo_max_side);
    if (data.photo_quality) setPhotoQuality(data.photo_quality);
    if (data.photo_input_mode) setPhotoInputMode(data.photo_input_mode);
    if (typeof data.owner_telegram === "string") setOwnerTelegram(data.owner_telegram);
    if (isOwner) {
      try {
        const [u, g] = await Promise.all([api.get("/users"), api.get("/owner/guest-links")]);
        setUsers(u.data || []);
        setGuestLinks(g.data || []);
      } catch (e) { console.warn("Settings: failed to load users/guest-links", e); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isOwner]);

  const createUser = async () => {
    setUserMsg(null);
    if (!newUser.email || !newUser.password) {
      setUserMsg({ type: "err", text: "Email e password sono obbligatori." }); return;
    }
    try {
      await api.post("/users", { email: newUser.email.toLowerCase(), password: newUser.password, name: newUser.name });
      setNewUser({ email: "", password: "", name: "" });
      const { data } = await api.get("/users");
      setUsers(data);
      setUserMsg({ type: "ok", text: "Utente creato." });
    } catch (e) {
      setUserMsg({ type: "err", text: formatApiError(e) });
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Eliminare questo utente?")) return;
    await api.delete(`/users/${id}`);
    const { data } = await api.get("/users");
    setUsers(data);
  };

  const resetUserPassword = async (id, email) => {
    if (!window.confirm(
      `Generare una password temporanea per ${email}?\n\n` +
      "La password apparirà UNA SOLA VOLTA. Copiala e inviala all'utente via Telegram.\n" +
      "La password attuale dell'utente verrà invalidata immediatamente."
    )) return;
    try {
      const { data } = await api.post(`/users/${id}/reset-password`);
      setResetResult({ email: data.user_email, password: data.temp_password });
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.password) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 2000);
    } catch (e) { console.warn("clipboard copy failed", e); }
  };

  const createGuestLink = async () => {
    const { data } = await api.post(`/owner/guest-links?label=${encodeURIComponent(newGuestLabel || "Ospite")}`);
    setGuestLinks((arr) => [data, ...arr]);
    setNewGuestLabel("");
  };

  const revokeGuestLink = async (token) => {
    if (!window.confirm("Revocare questo link? Chi lo possiede non potrà più accedere.")) return;
    await api.delete(`/owner/guest-links/${token}`);
    setGuestLinks((arr) => arr.filter((g) => g.id !== token));
  };

  const copyGuestUrl = async (token) => {
    const url = `${window.location.origin}/share/catalog/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(""), 2000);
    } catch (e) { console.warn("Settings: clipboard copy failed", e); }
  };

  const runReclassify = async (apply) => {
    setReclassifyLoading(true);
    try {
      const { data } = await api.post(`/artifacts/reclassify?apply=${apply ? "true" : "false"}`);
      setReclassifyPreview(data);
    } catch (e) {
      setReclassifyPreview({ error: e.response?.data?.detail || String(e) });
    } finally {
      setReclassifyLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // Always-allowed fields (per-user)
      const body = {
        gemini_model: model,
        gemini_key_preference: keyPreference,
        photo_input_mode: photoInputMode,
      };
      if (keyInput.trim()) body.gemini_api_key = keyInput.trim();
      // Owner-only fields — never send these from an editor, otherwise the
      // backend returns 403 and the whole save fails.
      if (isOwner) {
        body.photo_max_side = photoSide;
        body.photo_quality = photoQuality;
        body.owner_telegram = ownerTelegram.trim();
      }
      const { data } = await api.put("/settings", body);
      setSettings(data);
      setKeyInput("");
      setMsg({ type: "ok", text: "Impostazioni salvate." });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e) || "Errore nel salvataggio." });
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    if (!window.confirm("Rimuovere la tua chiave personale? Senza una chiave non potrai più identificare nuovi reperti finché non ne aggiungerai una.")) return;
    const { data } = await api.delete("/settings/gemini-key");
    setSettings(data);
    setMsg({ type: "ok", text: "Chiave personale rimossa." });
  };

  if (!settings) {
    return <div className="max-w-3xl mx-auto px-5 py-20"><div className="h-10 w-2/3 scanning" /></div>;
  }

  const usingPersonal = settings.active_provider === "personal";
  const usingShared = settings.active_provider === "shared";
  const sharedAvailable = settings.shared_key_available !== false;

  // Sidebar navigation items. Each entry maps to a section conditional
  // below; owner-only sections are filtered out for non-owner roles.
  const SECTIONS = [
    { id: "appearance", label: "Aspetto", icon: theme === "dark" ? Moon : Sun },
    { id: "ai", label: "AI e chiavi", icon: Sparkle },
    { id: "photos", label: "Foto", icon: ImageSquare },
    { id: "maps", label: "Le mie mappe", icon: MapPinIcon },
    { id: "sharing", label: "Condivisione", icon: ShareNetwork },
    { id: "backup", label: "Backup", icon: MicrosoftExcelLogo },
    ...(isOwner
      ? [
          { id: "users", label: "Utenti", icon: Users },
          { id: "recovery", label: "Recupero password", icon: TelegramLogo },
        ]
      : []),
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 fade-in">
      <div className="label-eyebrow mb-3">Impostazioni</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1E2520] mb-3">
        Personalizza la tua app
      </h1>
      <p className="text-[#5C635A] mb-10">
        Tema, qualità delle foto, motore AI: tutto configurabile da qui.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 md:gap-10">
        {/* Sidebar navigation */}
        <nav
          className="md:sticky md:top-24 md:self-start"
          data-testid="settings-sidebar"
        >
          <ul className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible -mx-1 px-1 pb-2 md:pb-0">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = activeSection === s.id;
              return (
                <li key={s.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm border transition-colors whitespace-nowrap ${
                      active
                        ? "bg-[#9C5B42] border-[#9C5B42] text-white"
                        : "country-tab-inactive"
                    }`}
                    data-testid={`settings-tab-${s.id}`}
                  >
                    <Icon
                      weight="duotone"
                      size={16}
                      className={active ? "text-white" : "text-[#9C5B42]"}
                    />
                    {s.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Section content */}
        <div className="min-w-0">

      {/* Tema */}
      {activeSection === "appearance" && (
      <div className="card p-6 md:p-8 mb-8">
        <div className="flex items-center gap-2 mb-4">
          {theme === "dark" ? <Moon weight="duotone" size={22} className="text-[#9C5B42]" /> : <Sun weight="duotone" size={22} className="text-[#9C5B42]" />}
          <h2 className="font-serif text-2xl text-[#1E2520]">Aspetto</h2>
        </div>
        <p className="text-sm text-[#5C635A] mb-5">
          Scegli tra tema chiaro (curato, da museo) o tema scuro (per uso serale e per esaltare le patine metalliche).
        </p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <button
            onClick={() => setTheme("light")}
            data-testid="theme-light-btn"
            className={`p-4 rounded-sm border-2 transition-all flex flex-col items-center gap-2 ${
              theme === "light" ? "border-[#9C5B42] bg-[#F0EBE1]" : "border-black/10 bg-white/40 hover:border-[#9C5B42]/40"
            }`}
          >
            <Sun weight="duotone" size={28} className="text-[#9C5B42]" />
            <span className="font-serif text-lg text-[#1E2520]">Chiaro</span>
          </button>
          <button
            onClick={() => setTheme("dark")}
            data-testid="theme-dark-btn"
            className={`p-4 rounded-sm border-2 transition-all flex flex-col items-center gap-2 ${
              theme === "dark" ? "border-[#9C5B42] bg-[#F0EBE1]" : "border-black/10 bg-white/40 hover:border-[#9C5B42]/40"
            }`}
          >
            <Moon weight="duotone" size={28} className="text-[#9C5B42]" />
            <span className="font-serif text-lg text-[#1E2520]">Scuro</span>
          </button>
        </div>
      </div>
      )}

      {/* Foto — visibile a tutti gli utenti. Contiene due blocchi:
          1. Modalità di acquisizione foto (per-utente: file vs file+camera)
          2. Compressione/qualità (owner-only, è impostazione globale) */}
      {activeSection === "photos" && (
      <div className="space-y-6 mb-8">
        {/* Modalità acquisizione foto — per tutti */}
        <div className="card p-6 md:p-8" data-testid="photo-input-section">
          <div className="flex items-center gap-2 mb-2">
            <ImageSquare weight="duotone" size={22} className="text-[#9C5B42]" />
            <h2 className="font-serif text-2xl text-[#1E2520]">Come carichi le foto</h2>
          </div>
          <p className="text-sm text-[#5C635A] mb-5 leading-relaxed">
            Scegli come vuoi caricare le foto durante l'identificazione e la
            modifica dei reperti.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label
              className={`relative border p-4 cursor-pointer transition-colors ${
                photoInputMode === "file"
                  ? "border-[#9C5B42] bg-[#FFF6EE]"
                  : "border-[#E5E0D8] bg-[#F9F8F6] hover:bg-[#F0EBE1]"
              }`}
              data-testid="photo-mode-file"
            >
              <input
                type="radio"
                name="photo-mode"
                value="file"
                checked={photoInputMode === "file"}
                onChange={() => setPhotoInputMode("file")}
                className="sr-only"
              />
              <div className="font-semibold text-[#1E2520]">Solo "Carica file"</div>
              <div className="text-xs text-[#5C635A] mt-1 leading-relaxed">
                Un solo bottone che apre la galleria o il file picker.
                <span className="italic"> Consigliato se preferisci scattare
                con l'app nativa della fotocamera e poi allegare.</span>
              </div>
            </label>
            <label
              className={`relative border p-4 cursor-pointer transition-colors ${
                photoInputMode === "camera"
                  ? "border-[#9C5B42] bg-[#FFF6EE]"
                  : "border-[#E5E0D8] bg-[#F9F8F6] hover:bg-[#F0EBE1]"
              }`}
              data-testid="photo-mode-camera-only"
            >
              <input
                type="radio"
                name="photo-mode"
                value="camera"
                checked={photoInputMode === "camera"}
                onChange={() => setPhotoInputMode("camera")}
                className="sr-only"
              />
              <div className="font-semibold text-[#1E2520]">Solo "Scatta foto"</div>
              <div className="text-xs text-[#5C635A] mt-1 leading-relaxed">
                Un solo bottone che apre direttamente la fotocamera del cellulare.
                <span className="italic"> Comodo se cataloghi tutto sul momento
                senza usare la galleria.</span>
              </div>
            </label>
            <label
              className={`relative border p-4 cursor-pointer transition-colors ${
                photoInputMode === "file_and_camera"
                  ? "border-[#9C5B42] bg-[#FFF6EE]"
                  : "border-[#E5E0D8] bg-[#F9F8F6] hover:bg-[#F0EBE1]"
              }`}
              data-testid="photo-mode-both"
            >
              <input
                type="radio"
                name="photo-mode"
                value="file_and_camera"
                checked={photoInputMode === "file_and_camera"}
                onChange={() => setPhotoInputMode("file_and_camera")}
                className="sr-only"
              />
              <div className="font-semibold text-[#1E2520]">Entrambi</div>
              <div className="text-xs text-[#5C635A] mt-1 leading-relaxed">
                Due bottoni separati: "Carica file" apre la galleria, "Scatta
                foto" apre la fotocamera. <span className="italic">Scegli ogni
                volta in base alla situazione.</span>
              </div>
            </label>
          </div>
        </div>

        {/* Compressione/qualità — solo owner */}
        {isOwner && (
          <div className="card p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <ImageSquare weight="duotone" size={22} className="text-[#9C5B42]" />
              <h2 className="font-serif text-2xl text-[#1E2520]">Qualità delle foto (globale)</h2>
            </div>
            <p className="text-sm text-[#5C635A] mb-5">
              Le foto vengono ricompresse al momento del caricamento. Riduci la dimensione per
              risparmiare spazio di archiviazione. La qualità impatta la nitidezza dei dettagli
              (es: leggende, patine). Questa impostazione vale per tutti gli utenti.
            </p>
            <div className="space-y-2 mb-6">
              {PHOTO_PRESETS.map((p) => {
                const active = photoSide === p.side && photoQuality === p.q;
                return (
                  <button
                    key={p.label}
                    onClick={() => { setPhotoSide(p.side); setPhotoQuality(p.q); }}
                    data-testid={`preset-${p.side}`}
                    className={`w-full text-left px-4 py-3 rounded-sm border-2 transition-all ${
                      active ? "border-[#9C5B42] bg-[#F0EBE1]" : "border-black/10 hover:border-[#9C5B42]/40"
                    }`}
                  >
                    <div className="text-sm text-[#1E2520]">{p.label}</div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-eyebrow block mb-2">Lato max (px)</label>
                <input
                  type="number"
                  min="600"
                  max="3200"
                  className="input-base"
                  value={photoSide}
                  onChange={(e) => setPhotoSide(parseInt(e.target.value) || 1600)}
                  data-testid="photo-side-input"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Qualità JPEG (50-95)</label>
                <input
                  type="number"
                  min="50"
                  max="95"
                  className="input-base"
                  value={photoQuality}
                  onChange={(e) => setPhotoQuality(parseInt(e.target.value) || 88)}
                  data-testid="photo-quality-input"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* AI & Chiave Gemini — sezione unificata
          Spiega A COSA serve la chiave, mostra QUALE è attiva, permette
          di scegliere fra condivisa e personale, e contiene il form di
          caricamento + selettore modello. Tutto in un solo card per
          minimizzare la confusione. */}
      {activeSection === "ai" && (
      <div className="card p-6 md:p-8 mb-8" data-testid="ai-key-section">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle weight="duotone" size={22} className="text-[#9C5B42]" />
          <h2 className="font-serif text-2xl text-[#1E2520]">AI e chiave Gemini</h2>
        </div>

        {/* Spiegazione introduttiva — tutto in un paragrafo, senza box.
            L'utente legge in pochi secondi cosa serve e cosa fare. */}
        <p className="text-sm text-[#1E2520] leading-relaxed mb-6">
          Reperti usa <strong>Google Gemini</strong> per riconoscere le tue foto e
          generare identificazione, datazione, descrizione storica e valore stimato.
          Per chiamare Google serve una <strong>chiave API</strong> (è il "biglietto"
          che dice a Google chi sta facendo le richieste e quante può farne gratis).
          <span className="text-[#5C635A]">
            {" "}Scegli sotto fra la <strong>chiave condivisa</strong> di Reperti
            (zero configurazione, quota in comune con gli altri utenti) e una{" "}
            <strong>chiave personale gratuita</strong> creata da te su Google AI
            Studio (quote più ampie e indipendenti).
          </span>
        </p>

        {/* Scegli quale chiave usare. Le card mostrano direttamente lo stato
            attuale: quella selezionata è anche quella in uso, e include
            sotto il titolo modello/preview chiave. Niente box "stato"
            separato. */}
        <label className="label-eyebrow block mb-2">Quale chiave vuoi usare?</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
          {/* Shared option */}
          <label
            className={`relative border p-4 cursor-pointer transition-colors ${
              keyPreference === "shared"
                ? "border-[#9C5B42] bg-[#FFF6EE]"
                : "border-[#E5E0D8] bg-[#F9F8F6] hover:bg-[#F0EBE1]"
            }`}
            data-testid="key-pref-shared"
          >
            <input
              type="radio"
              name="key-pref"
              value="shared"
              checked={keyPreference === "shared"}
              onChange={() => setKeyPreference("shared")}
              className="sr-only"
              disabled={!sharedAvailable}
            />
            <div className="flex items-start gap-3">
              <Sparkle
                weight="duotone"
                size={22}
                className={keyPreference === "shared" ? "text-[#9C5B42]" : "text-[#5C635A]"}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-[#1E2520]">Chiave condivisa Reperti</div>
                  {usingShared && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[#9C5B42] text-white">
                      ATTIVA
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#5C635A] mt-1 leading-relaxed">
                  Pronta all'uso, zero configurazione. <strong>Quota condivisa</strong> con tutti
                  gli utenti — se in molti identificano nello stesso giorno potresti vedere errori
                  "rate limit". Ideale per iniziare o per chi cataloga poco.
                </div>
              </div>
            </div>
          </label>

          {/* Personal option */}
          <label
            className={`relative border p-4 cursor-pointer transition-colors ${
              keyPreference === "personal"
                ? "border-[#9C5B42] bg-[#FFF6EE]"
                : "border-[#E5E0D8] bg-[#F9F8F6] hover:bg-[#F0EBE1]"
            }`}
            data-testid="key-pref-personal"
          >
            <input
              type="radio"
              name="key-pref"
              value="personal"
              checked={keyPreference === "personal"}
              onChange={() => setKeyPreference("personal")}
              className="sr-only"
            />
            <div className="flex items-start gap-3">
              <Key
                weight="duotone"
                size={22}
                className={keyPreference === "personal" ? "text-[#9C5B42]" : "text-[#5C635A]"}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-[#1E2520]">Chiave personale Google AI Studio</div>
                  {usingPersonal && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[#9C5B42] text-white">
                      ATTIVA · {settings.gemini_key_preview}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#5C635A] mt-1 leading-relaxed">
                  <strong>Quote più ampie e tutte tue</strong> (~1.500-3.000 identificazioni/giorno
                  con Flash). Da creare gratis su Google AI Studio in 2 minuti.
                </div>
              </div>
            </div>
          </label>
        </div>

        {/* Personal key form — visible only when "personal" is selected OR
            already has a personal key stored (so they can replace/remove it). */}
        {(keyPreference === "personal" || settings.has_personal_gemini_key) && (
          <div className="border-t border-[#E5E0D8] pt-5 mt-6" data-testid="personal-key-form">
            <label className="label-eyebrow block mb-2">
              {settings.has_personal_gemini_key ? "Sostituisci la tua chiave personale" : "Incolla la tua chiave personale"}
            </label>
            <input
              type="password"
              className="input-base mb-3"
              placeholder={settings.has_personal_gemini_key ? "Lascia vuoto per mantenere la chiave attuale" : "AIza..."}
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              data-testid="gemini-key-input"
              autoComplete="off"
            />
            <p className="text-xs text-[#5C635A] mb-3 leading-relaxed">
              Non hai ancora una chiave? Creala gratis su{" "}
              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noreferrer"
                className="text-[#9C5B42] inline-flex items-center gap-1 underline"
                data-testid="gemini-key-howto-link"
              >
                Google AI Studio <ArrowSquareOut size={11} />
              </a>{" "}
              (serve solo un account Google, ~2 minuti). Poi torna qui e incollala sopra.
            </p>

            {/* Single collapsible "approfondisci" with privacy + propagation
                info — keeps the surface clean but the details one click away. */}
            <details className="mb-3 text-sm" data-testid="key-details">
              <summary className="cursor-pointer text-[#9C5B42] inline-flex items-center gap-1 select-none">
                <ShieldCheck weight="duotone" size={16} /> Sicurezza e attivazione — leggi prima di salvare
              </summary>
              <div className="mt-3 space-y-3 text-[#5C635A] leading-relaxed pl-5">
                <div>
                  <strong className="text-[#1E2520]">La tua chiave è al sicuro.</strong>{" "}
                  È salvata in modo cifrato sul tuo account, usata solo per identificare i tuoi
                  reperti. Nessun altro utente può vederla, non viene mai mostrata di nuovo in
                  chiaro (qui mostriamo solo le ultime 4 cifre). Google la collega esclusivamente
                  al tuo account: non dà accesso ad altri tuoi servizi.
                </div>
                <div>
                  <strong className="text-[#1E2520]">Attivazione fino a 10 minuti.</strong>{" "}
                  Google può impiegare qualche minuto per attivare una chiave appena creata. Se le
                  prime identificazioni danno errore <em>"API key not valid"</em> o <em>404</em>,
                  aspetta 5-10 minuti e riprova — non serve generare una nuova chiave.
                </div>
              </div>
            </details>

            {keyPreference === "personal" && !settings.has_personal_gemini_key && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-3 flex gap-2 text-sm text-amber-900"
                data-testid="personal-key-missing-notice"
              >
                <Warning weight="duotone" size={18} className="text-amber-700 shrink-0 mt-0.5" />
                <div>
                  Hai scelto la chiave personale ma non l'hai ancora salvata. Fino ad allora il
                  sistema userà la chiave condivisa come fallback.
                </div>
              </div>
            )}

            {settings.has_personal_gemini_key && (
              <button
                onClick={removeKey}
                className="btn-ghost inline-flex items-center gap-2 border-red-300 text-red-700 hover:bg-red-50 text-xs"
                data-testid="remove-key-btn"
              >
                <Trash weight="duotone" size={14} /> Rimuovi chiave personale
              </button>
            )}
          </div>
        )}

        {/* Modello — filtraggio per ruolo e tipo di chiave:
            - owner (admin): tutti i modelli (incluso 3.5 Flash frontier)
            - editor: solo modelli non-owner (2.5 Flash + 3.1 Flash-Lite) */}
        {(() => {
          const availableModels = MODELS.filter((m) => {
            if (isOwner) return true;
            return !m.ownerOnly;
          });

          // If the user has saved a model that's no longer available to them
          // (e.g. they had Pro and switched to shared), auto-realign the
          // local state so the dropdown reflects what's actually saved.
          const valid = availableModels.some((m) => m.id === model);
          if (!valid && availableModels.length > 0 && model !== availableModels[0].id) {
            // setState during render is fine because React will batch.
            // The next save will persist this choice.
            setTimeout(() => setModel(availableModels[0].id), 0);
          }
          const selected = availableModels.find((m) => m.id === model) || availableModels[0];

          return (
            <div className="border-t border-[#E5E0D8] pt-5 mt-6">
              <label className="label-eyebrow block mb-2">Modello Gemini</label>
              <select
                className="input-base mb-3"
                value={selected?.id || ""}
                onChange={(e) => setModel(e.target.value)}
                data-testid="gemini-model-select"
              >
                {availableModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} · {m.quota}
                  </option>
                ))}
              </select>

              {selected && (
                <p className="text-xs text-[#5C635A] leading-relaxed">
                  <strong className="text-[#1E2520]">{selected.short}</strong> — {selected.description}
                </p>
              )}

              {!isOwner && keyPreference === "shared" && (
                <p className="text-[11px] text-[#5C635A] leading-relaxed mt-2 italic">
                  Con la chiave condivisa è disponibile solo Flash, per non saturare la quota
                  comune. Per accedere a Flash-Lite passa alla chiave personale.
                </p>
              )}

              <p className="text-[11px] text-[#5C635A] leading-relaxed mt-3">
                Quote del piano gratuito Google AI Studio (aggiornate a inizio 2026), possono cambiare:
                vedi{" "}
                <a
                  href="https://ai.google.dev/pricing"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#9C5B42] underline inline-flex items-center gap-1"
                >
                  ai.google.dev/pricing <ArrowSquareOut size={10} />
                </a>
                . A quota esaurita le identificazioni tornano disponibili dopo la mezzanotte UTC.
              </p>
            </div>
          );
        })()}
      </div>
      )}

      {/* Save bar — visible for sections that mutate settings (appearance,
          ai, photos, recovery). My-Maps / users / sharing / backup have
          their own actions inline. */}
      {["appearance", "ai", "photos", "recovery"].includes(activeSection) && (
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          {saving ? "Salvataggio..." : "Salva impostazioni"}
        </button>
      </div>
      )}

      {msg && (
        <div
          className={`px-4 py-3 rounded-sm text-sm flex items-center gap-2 ${
            msg.type === "ok" ? "bg-[#F0EBE1] text-[#1E2520]" : "bg-red-50 text-red-800 border border-red-200"
          }`}
          data-testid="settings-message"
        >
          {msg.type === "ok" ? <CheckCircle weight="duotone" size={18} /> : <Warning weight="duotone" size={18} />}
          {msg.text}
        </div>
      )}

      {activeSection === "ai" && (
      <div className="mt-8 border border-[#9C5B42]/30 bg-[#F0EBE1]/50 p-5 rounded-sm text-sm text-[#1E2520]">
        <strong>Sicurezza:</strong> la chiave è salvata solo sul tuo backend privato (MongoDB),
        non viene mai mostrata in chiaro nell'interfaccia dopo il salvataggio e non lascia mai il server.
      </div>
      )}

      {/* My Maps (Google KML import) — available to all users */}
      {activeSection === "maps" && <MyMapsSection />}

      {isOwner && (
        <>
          {/* Recovery contact (Telegram) */}
          {activeSection === "recovery" && (
          <div className="card p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <TelegramLogo weight="duotone" size={22} className="text-[#9C5B42]" />
              <h2 className="font-serif text-2xl text-[#1E2520]">Contatto recupero password</h2>
            </div>
            <p className="text-sm text-[#5C635A] mb-4">
              Imposta qui il tuo username Telegram pubblico. Verrà mostrato sulla pagina di login
              agli utenti che cliccano "Ho perso la password", così sapranno come contattarti per
              ricevere una password temporanea. <strong>Inviala solo via Telegram</strong>, mai per
              email o in chiaro.
            </p>
            <label className="label-eyebrow block mb-2">Username Telegram (es. @miohandle)</label>
            <input
              type="text"
              className="input-base"
              value={ownerTelegram}
              onChange={(e) => setOwnerTelegram(e.target.value)}
              placeholder="@mio_username_telegram"
              maxLength={64}
              data-testid="owner-telegram-input"
            />
            <p className="text-xs text-[#5C635A] mt-2">
              Lascia vuoto per nascondere il link di recupero (gli utenti non potranno richiederti
              una password temporanea). Le modifiche si applicano dopo aver cliccato
              "Salva tutte le impostazioni".
            </p>
          </div>
          )}

          {/* Users */}
          {activeSection === "users" && (
          <div className="card p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Users weight="duotone" size={22} className="text-[#9C5B42]" />
              <h2 className="font-serif text-2xl text-[#1E2520]">Utenti autorizzati</h2>
            </div>
            <p className="text-sm text-[#5C635A] mb-5">
              Tutti gli utenti registrati. Ogni utente ha un catalogo isolato — vedi qui sotto quanti
              reperti hanno catalogato e se hanno configurato la loro chiave Gemini personale.
              I dati sensibili (password, chiave API) sono nascosti.
            </p>

            <ul className="mb-6 divide-y divide-black/5" data-testid="users-list">
              {users.map((u) => {
                const created = u.created_at ? new Date(u.created_at) : null;
                const createdStr = created && !isNaN(created) ? created.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "";
                return (
                  <li key={u.id} className="py-3 flex items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[#1E2520] truncate font-medium">{u.email}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-sm ${u.role === "owner" ? "bg-[#9C5B42] text-white" : "bg-[#F0EBE1] text-[#5C635A]"}`}>
                          {u.role === "owner" ? "Proprietario" : "Editor"}
                        </span>
                        {u.has_gemini_key ? (
                          <span className="text-xs text-[#5C635A] inline-flex items-center gap-1" title="L'utente ha caricato una propria chiave Google AI Studio">
                            <Key size={11} /> chiave personale
                          </span>
                        ) : (
                          <span className="text-xs text-[#5C635A] inline-flex items-center gap-1" title="L'utente usa la chiave condivisa Reperti (default)">
                            <Sparkle size={11} /> chiave condivisa
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#5C635A] mt-0.5">
                        {u.name ? `${u.name} · ` : ""}
                        <span className="font-mono">{u.artifact_count ?? 0}</span> reperti
                        {createdStr ? ` · iscritto il ${createdStr}` : ""}
                      </div>
                    </div>
                    {u.role !== "owner" && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => resetUserPassword(u.id, u.email)}
                          className="text-[#5C635A] hover:text-[#9C5B42] hover:bg-[#F0EBE1] p-2 rounded-sm"
                          title="Genera password temporanea"
                          data-testid={`reset-pwd-${u.id}`}
                        >
                          <ArrowsClockwise size={18} />
                        </button>
                        <button
                          onClick={() => deleteUser(u.id)}
                          className="text-red-700 hover:bg-red-50 p-2 rounded-sm"
                          title="Elimina utente"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="label-eyebrow mb-2">Aggiungi un utente manualmente</div>
            <p className="text-xs text-[#5C635A] mb-3">
              Puoi creare un utente direttamente da qui (oltre alla registrazione pubblica
              che chiunque può usare dalla schermata di accesso).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <input
                type="email"
                className="input-base"
                placeholder="email@esempio.it"
                value={newUser.email}
                onChange={(e) => setNewUser((s) => ({ ...s, email: e.target.value }))}
                data-testid="new-user-email"
              />
              <input
                type="text"
                className="input-base"
                placeholder="Nome (opzionale)"
                value={newUser.name}
                onChange={(e) => setNewUser((s) => ({ ...s, name: e.target.value }))}
                data-testid="new-user-name"
              />
              <input
                type="password"
                className="input-base"
                placeholder="Password (min 6 caratteri)"
                value={newUser.password}
                onChange={(e) => setNewUser((s) => ({ ...s, password: e.target.value }))}
                data-testid="new-user-password"
              />
            </div>
            <button
              onClick={createUser}
              className="btn-primary inline-flex items-center gap-2 text-sm"
              data-testid="create-user-btn"
            >
              <Plus size={16} /> Aggiungi utente
            </button>
            {userMsg && (
              <div
                className={`mt-4 px-4 py-3 rounded-sm text-sm ${
                  userMsg.type === "ok" ? "bg-[#F0EBE1] text-[#1E2520]" : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {userMsg.text}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* Guest Links — available to all users (each user manages their own
          guest catalog links). */}
      {activeSection === "sharing" && (
      <div className="card p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <ShareNetwork weight="duotone" size={22} className="text-[#9C5B42]" />
              <h2 className="font-serif text-2xl text-[#1E2520]">Link ospite (sola lettura)</h2>
            </div>
            <p className="text-sm text-[#5C635A] mb-5">
              Genera un link da inviare a chi vuoi: vedrà l'intero catalogo in <strong>sola lettura</strong>,
              senza poter scansionare, modificare o accedere alle impostazioni. Puoi revocare il link in qualsiasi momento.
            </p>

            <div className="flex gap-2 mb-5">
              <input
                type="text"
                className="input-base flex-1"
                placeholder="Etichetta (es: 'Marco', 'Forum metaldetector')"
                value={newGuestLabel}
                onChange={(e) => setNewGuestLabel(e.target.value)}
                data-testid="new-guest-label"
              />
              <button
                onClick={createGuestLink}
                className="btn-primary inline-flex items-center gap-2 text-sm"
                data-testid="create-guest-link-btn"
              >
                <Plus size={16} /> Genera link
              </button>
            </div>

            <ul className="divide-y divide-black/5" data-testid="guest-links-list">
              {guestLinks.length === 0 && (
                <li className="text-sm text-[#5C635A] py-3 italic">Nessun link generato.</li>
              )}
              {guestLinks.map((g) => {
                const url = `${window.location.origin}/share/catalog/${g.id}`;
                return (
                  <li key={g.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[#1E2520] font-medium truncate">{g.label}</div>
                      <div className="text-xs text-[#5C635A] font-mono truncate">{url}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyGuestUrl(g.id)}
                        className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1"
                        data-testid={`copy-guest-${g.id}`}
                      >
                        <Copy size={14} /> {copiedToken === g.id ? "Copiato!" : "Copia"}
                      </button>
                      <button
                        onClick={() => revokeGuestLink(g.id)}
                        className="text-red-700 hover:bg-red-50 p-2 rounded-sm"
                        title="Revoca"
                        data-testid={`revoke-guest-${g.id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          )}

          {/* Excel export / import — available to all users (their own
              catalog data only). */}
          {activeSection === "backup" && (
          <div className="card p-6 md:p-8 mb-8" data-testid="catalog-io-card">
            <div className="flex items-center gap-2 mb-4">
              <MicrosoftExcelLogo weight="duotone" size={22} className="text-[#9C5B42]" />
              <h2 className="font-serif text-2xl text-[#1E2520]">Backup catalogo (Excel)</h2>
            </div>
            <p className="text-sm text-[#5C635A] mb-5">
              Esporta l'intero catalogo in un file Excel come backup o per analizzarlo offline.
              Puoi anche reimportare un file Excel (le righe con un <code className="font-mono text-xs">ID interno</code> noto
              aggiornano i reperti esistenti, le nuove righe vengono inserite). Le foto restano
              caricate solo nell'app.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                onClick={exportCatalog}
                disabled={exporting}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="export-catalog-btn"
              >
                <DownloadSimple weight="duotone" size={18} />
                {exporting ? "Esportazione…" : "Esporta tutto in Excel"}
              </button>

              <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer" data-testid="import-catalog-btn">
                <UploadSimple weight="duotone" size={18} />
                {importing ? "Importazione…" : "Importa da Excel"}
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={importCatalog}
                  className="hidden"
                  data-testid="import-catalog-input"
                />
              </label>
            </div>

            <label className="flex items-start gap-2 text-sm text-[#5C635A] mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={importDryRun}
                onChange={(e) => setImportDryRun(e.target.checked)}
                className="mt-1"
                data-testid="import-dry-run-checkbox"
              />
              <span>
                <strong>Prova senza salvare</strong> — analizza il file e mostra cosa verrebbe importato,
                senza modificare il database. Disattiva questa opzione per applicare davvero le modifiche.
              </span>
            </label>

            {importResult && (
              <div className="mt-4 p-4 rounded-sm border border-[#E5E0D8] bg-[#F9F8F6]" data-testid="import-result">
                {importResult.error ? (
                  <div className="text-red-700">Errore: {importResult.error}</div>
                ) : (
                  <div className="text-sm text-[#1E2520] space-y-1">
                    <div>
                      {importResult.dry_run ? "🧪 Anteprima:" : "✅ Importazione completata:"}{" "}
                      <strong>{importResult.total_rows}</strong> righe trovate
                    </div>
                    <div>
                      Inserite: <strong className="text-[#9C5B42]">{importResult.inserted}</strong> ·
                      Aggiornate: <strong className="text-[#9C5B42]">{importResult.updated}</strong> ·
                      Saltate: <strong>{importResult.skipped}</strong>
                    </div>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-red-700">
                          {importResult.errors.length} errori
                        </summary>
                        <ul className="text-xs text-red-700 mt-1 list-disc pl-5">
                          {importResult.errors.map((e, i) => (
                            <li key={`import-err-${i}-${String(e).slice(0, 32)}`}>{e}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Bulk reclassify — fixes historical misclassifications after
              category-rule updates (e.g. devotional rings, medicine caps). */}
          {activeSection === "backup" && (
          <div className="card p-6 md:p-8 mb-8" data-testid="reclassify-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle weight="duotone" size={22} className="text-[#9C5B42]" />
              <h2 className="font-serif text-2xl text-[#1E2520]">Riclassifica catalogo</h2>
            </div>
            <p className="text-sm text-[#5C635A] mb-5">
              Rilegge tutti i tuoi reperti e applica le regole di categorizzazione più recenti.
              Utile dopo aggiornamenti alle regole (es. anelli devozionali che erano finiti in
              Devozionale, tappi/oggetti con santi che ora vanno in Vario). <strong>I codici
              inventariali non vengono modificati</strong> — se un reperto cambia categoria puoi
              riassegnarlo manualmente dal suo dettaglio.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={() => runReclassify(false)}
                disabled={reclassifyLoading}
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="reclassify-preview-btn"
              >
                {reclassifyLoading ? "Analisi…" : "Anteprima modifiche"}
              </button>
              {reclassifyPreview && reclassifyPreview.would_change > 0 && !reclassifyPreview.applied && (
                <button
                  onClick={() => runReclassify(true)}
                  disabled={reclassifyLoading}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="reclassify-apply-btn"
                >
                  Applica {reclassifyPreview.would_change} modifiche
                </button>
              )}
            </div>

            {reclassifyPreview && (
              <div className="mt-2 p-4 rounded-sm border border-[#E5E0D8] bg-[#F9F8F6]" data-testid="reclassify-result">
                {reclassifyPreview.error ? (
                  <div className="text-red-700 text-sm">Errore: {reclassifyPreview.error}</div>
                ) : (
                  <div className="text-sm text-[#1E2520] space-y-2">
                    <div>
                      {reclassifyPreview.applied ? "✅ Applicato:" : "🧪 Anteprima:"}{" "}
                      Analizzati <strong>{reclassifyPreview.total_scanned}</strong> reperti,{" "}
                      <strong className="text-[#9C5B42]">{reclassifyPreview.would_change}</strong>{" "}
                      {reclassifyPreview.applied ? "aggiornati" : "cambierebbero categoria"}.
                    </div>
                    {reclassifyPreview.changes && reclassifyPreview.changes.length > 0 && (
                      <details open={reclassifyPreview.changes.length <= 20}>
                        <summary className="cursor-pointer text-xs text-[#5C635A]">
                          Dettaglio ({reclassifyPreview.changes.length})
                        </summary>
                        <ul className="text-xs text-[#1E2520] mt-2 space-y-1 max-h-64 overflow-auto">
                          {reclassifyPreview.changes.map((ch) => (
                            <li key={ch.id} className="flex flex-wrap items-baseline gap-x-2">
                              <span className="font-mono text-[#9C5B42]">{ch.inventory_code || "—"}</span>
                              <span className="font-medium">{ch.nome || "Senza nome"}</span>
                              <span className="text-[#5C635A]">
                                {ch.old_categoria || "?"} → <strong className="text-[#1E2520]">{ch.new_categoria}</strong>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}


        </div>{/* end content wrapper */}
      </div>{/* end grid */}

      {/* Temp password reveal modal (owner only) — shown once after a
          password reset. The plaintext is never persisted on the server. */}
      {resetResult && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center px-4"
          onClick={() => setResetResult(null)}
          data-testid="reset-result-modal"
        >
          <div
            className="card max-w-md w-full p-6 md:p-7 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setResetResult(null)}
              className="absolute top-3 right-3 text-[#5C635A] hover:text-[#1E2520]"
              aria-label="Chiudi"
              data-testid="reset-result-close"
            >
              <X size={20} />
            </button>
            <h2 className="font-serif text-2xl text-[#1E2520] mb-3">Password temporanea generata</h2>
            <p className="text-sm text-[#5C635A] mb-4">
              Per l'utente <strong className="font-mono">{resetResult.email}</strong>. Copiala
              ora e inviala via Telegram. <strong>Non sarà più visibile dopo aver chiuso questa
              finestra.</strong>
            </p>

            <div className="rounded-md border-2 border-[#9C5B42] bg-[#F9F8F6] p-4 mb-4 flex items-center justify-between gap-3">
              <code
                className="font-mono text-xl text-[#1E2520] select-all break-all"
                data-testid="reset-temp-password"
              >
                {resetResult.password}
              </code>
              <button
                onClick={copyTempPassword}
                className="btn-primary inline-flex items-center gap-1.5 text-sm shrink-0"
                data-testid="reset-copy-btn"
              >
                <Copy size={14} />
                {resetCopied ? "Copiata!" : "Copia"}
              </button>
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 mb-4">
              L'utente verrà obbligato a impostare una nuova password al primo accesso. La password
              vecchia non funziona più.
            </div>

            <button
              onClick={() => setResetResult(null)}
              className="btn-ghost w-full"
              data-testid="reset-result-done"
            >
              Ho copiato, chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// =====================================================================
// MyMapsSection — manages KML/KMZ uploads for the user.
// Kept as a sibling component so the main Settings file stays readable
// and the upload state never collides with the rest of the page.
// =====================================================================
function MyMapsSection() {
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/my-maps/layers");
      setLayers(data || []);
    } catch (e) {
      console.warn("MyMaps: failed to load layers", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("Il file supera i 10 MB. Esporta da Google My Maps con meno punti o suddividi in più file.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/my-maps/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Eliminare il layer "${name}"? I punti importati non saranno più visibili sulla mappa.`)) return;
    try {
      await api.delete(`/my-maps/layers/${id}`);
      await load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  return (
    <div className="card p-6 md:p-8 mt-10" data-testid="my-maps-section">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle weight="duotone" size={20} className="text-[#9C5B42]" />
        <h2 className="font-serif text-2xl text-[#1E2520]">Le mie mappe (Google My Maps)</h2>
      </div>
      <p className="text-sm text-[#5C635A] mb-4 leading-relaxed">
        Importa le tue mappe personali da Google. Hai due opzioni:
      </p>
      <ul className="text-sm text-[#5C635A] mb-4 space-y-1 list-disc pl-5 leading-relaxed">
        <li>
          <strong>Google My Maps</strong> (<a href="https://mymaps.google.com" target="_blank" rel="noreferrer" className="underline text-[#9C5B42]">mymaps.google.com</a>):
          apri la tua mappa → menu ⋮ → "Esporta in KML/KMZ".
        </li>
        <li>
          <strong>Google Maps (preferiti / elenchi)</strong>: usa{" "}
          <a href="https://takeout.google.com" target="_blank" rel="noreferrer" className="underline text-[#9C5B42]">Google Takeout</a>{" "}
          → seleziona solo "Maps (i tuoi luoghi)" e "Saved" → ricevi un file <code className="text-[11px] bg-white px-1 rounded-sm">.csv</code> o <code className="text-[11px] bg-white px-1 rounded-sm">.json</code> da caricare qui.
        </li>
      </ul>
      <p className="text-xs text-[#5C635A] mb-4">
        Formati supportati: <strong>.kml, .kmz, .csv, .json</strong>. Ogni file diventa un layer separato.
      </p>

      <label
        htmlFor="mymaps-upload"
        className={`block border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
          uploading ? "border-[#9C5B42] bg-[#FFF6EE]" : "border-[#E5E0D8] hover:border-[#9C5B42] hover:bg-[#FFF6EE]"
        }`}
        data-testid="mymaps-upload-label"
      >
        <input
          id="mymaps-upload"
          type="file"
          accept=".kml,.kmz,.csv,.json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,text/csv,application/json"
          className="hidden"
          disabled={uploading}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
          data-testid="mymaps-upload-input"
        />
        <UploadSimple weight="duotone" size={32} className="mx-auto mb-2 text-[#9C5B42]" />
        <div className="font-medium text-[#1E2520]">
          {uploading ? "Caricamento in corso..." : "Clicca per caricare un file .kml / .kmz / .csv / .json"}
        </div>
        <div className="text-xs text-[#5C635A] mt-1">Max 10 MB, fino a 5'000 punti per file.</div>
      </label>

      {error && (
        <div className="mt-3 border border-red-300 bg-red-50 text-red-800 px-4 py-3 rounded-sm text-sm" data-testid="mymaps-upload-error">
          {error}
        </div>
      )}

      <div className="mt-5">
        <div className="label-eyebrow mb-3">Layer caricati ({layers.length})</div>
        {loading ? (
          <div className="text-sm text-[#5C635A]">Caricamento...</div>
        ) : layers.length === 0 ? (
          <div className="text-sm text-[#5C635A] italic">Non hai ancora importato nessuna mappa.</div>
        ) : (
          <ul className="space-y-2">
            {layers.map((l) => (
              <li
                key={l.id}
                className="flex items-center gap-3 px-4 py-3 border border-[#E5E0D8] bg-[#F9F8F6] rounded-sm"
                data-testid={`mymaps-layer-${l.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1E2520] truncate">{l.name}</div>
                  <div className="text-xs text-[#5C635A] truncate">
                    {l.point_count} punti · {l.source_filename || "—"}
                  </div>
                </div>
                <button
                  onClick={() => remove(l.id, l.name)}
                  className="text-red-700 hover:bg-red-50 p-2 rounded-sm shrink-0"
                  title="Elimina layer"
                  data-testid={`mymaps-delete-${l.id}`}
                >
                  <Trash size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
